import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react'
import { memo } from 'react'

import { Report } from 'api/types'

import useTracking from 'tracking/useTracking'
import { useNavigate } from 'react-router-dom'
import { useAppParams, useCanUseBetaFeatures } from 'routes/utils'
import resolvePath from 'utils/resolvePath'
import { DownloadPdfReport, ViewPdfReport } from 'components'
import { Download } from '@carbon/icons-react'
import { useClientType } from 'contexts/ClientType'

export const DownloadDataFile = ({
  children,
  report,
  variant = 'outline',
  ...buttonProps
}: {
  report: Report
  variant?: string
  children?: any
  [key: string]: any // Allow additional props
}) => {
  const { isGov } = useClientType()
  function handleClick() {
    if (report?.reportDataFile) {
      const link = document.createElement('a')
      link.href = report.reportDataFile
      link.download = report.reportDataFile.split('/').pop() || 'Data File'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
  if (!report?.reportDataFile || !isGov) return null
  return (
    <Button onClick={handleClick} variant={variant} ml={4} {...buttonProps}>
      {children ?? 'Download Data File'}
    </Button>
  )
}

function ReportViewOrDownload({ report }: { report: Report }) {
  const navigate = useNavigate()
  const { app, pageSlug, subPageSlug } = useAppParams()
  const userCanAccessBetaFeatures = useCanUseBetaFeatures()

  const [tracking] = useTracking()
  if (!report) {
    return null
  }

  const handleReportNavigation = () => {
    //TODO: add tracking
    if (!subPageSlug) {
      const path = resolvePath([app, pageSlug, 'reports', report.id])
      navigate(`${path}?type=analyst-updates`)
    } else {
      const path = resolvePath([app, pageSlug, subPageSlug, report.id])
      navigate(`${path}?type=analyst-updates`)
    }
  }

  return (
    <Box borderBottomRadius='8px' bg='gray.50'>
      <Flex justifyContent='flex-end' px={5} alignItems='center'>
        {userCanAccessBetaFeatures ? (
          <Button onClick={handleReportNavigation} variant='outline' my='4px'>
            View Report
          </Button>
        ) : (
          <>
            <DownloadPdfReport
              url={report.report}
              title={report.title}
              onDownload={() => {
                tracking.downloadReport({
                  report: report.title,
                })
              }}
              triggerComponent={({ isLoading, disabled, handleClick }) => (
                <Flex
                  fontWeight={500}
                  fontSize='sm'
                  lineHeight='none'
                  alignItems='center'
                  {...(isLoading
                    ? {
                        textDecor: 'none',
                        cursor: 'default',
                        _hover: { textDecor: 'none' },
                      }
                    : {})}
                >
                  <Box
                    py={4}
                    borderTop='1px solid'
                    borderColor='gray.100'
                    display='flex'
                    alignItems='center'
                  >
                    <Box
                      width='32px'
                      height='32px'
                      mr={2}
                      display='flex'
                      bg='white'
                      rounded='full'
                      shadow='sm'
                      alignItems='center'
                      justifyContent='center'
                      as='button'
                      onClick={handleClick}
                      disabled={disabled}
                    >
                      <Download size={20} />
                    </Box>
                    <Text
                      fontWeight={500}
                      fontSize='sm'
                      lineHeight='none'
                      pr={2}
                    >
                      {report.title}
                    </Text>
                  </Box>
                  {isLoading && <Spinner ml={2} />}
                </Flex>
              )}
            />
            <ViewPdfReport
              url={report.report}
              onView={() => {
                tracking.openReport({
                  report: report.title,
                })
              }}
            />{' '}
          </>
        )}
        <DownloadDataFile report={report} />
      </Flex>
    </Box>
  )
}

export default memo(ReportViewOrDownload)
